// CampusHero — the club's evolving hero illustration (one per level, with
// a regional variant when the art exists), PC Futbol stadium-section style.
// Falls back to the neutral art and finally to a branded gradient.

import { useGameStore } from '@/store/gameStore'
import { getClubHero, regionalAssetCandidates } from '@/config/campusConfig'
import { CLUB_LEVELS } from '@/config/clubLevelConfig'
import { TierRevealClip } from './TierRevealClip'
import { FacilityPlaceholder } from './FacilityPlaceholder'
import { AssetImage } from './AssetImage'
import { Building2, Star } from 'lucide-react'

interface CampusHeroProps {
  compact?: boolean // smaller variant for the Dashboard header
}

export function CampusHero({ compact = false }: CampusHeroProps) {
  const clubLevel = useGameStore(s => s.clubLevel)
  const reputation = useGameStore(s => s.reputation)
  const region = useGameStore(s => s.region)
  const hero = getClubHero(clubLevel)
  const levelInfo = CLUB_LEVELS.find(l => l.level === clubLevel)

  return (
    <div className="campus-hero" style={compact ? { aspectRatio: '16 / 5' } : undefined}>
      <TierRevealClip tierKey={clubLevel}>
        <AssetImage
          sources={regionalAssetCandidates(hero.image, region)}
          alt={hero.name}
          fallback={
            <FacilityPlaceholder
              gradient={['#0D1520', '#1E3A5F']}
              icon={Building2}
              caption={hero.name}
            />
          }
        />
      </TierRevealClip>

      <div className="campus-hero-overlay">
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.6rem', flexWrap: 'wrap' }}>
          <span style={{
            fontFamily: 'var(--font-titles)',
            fontWeight: 800,
            fontSize: compact ? '1.05rem' : '1.5rem',
            color: '#fff',
            textShadow: '0 1px 4px rgba(0,0,0,0.6)',
          }}>
            {hero.name}
          </span>
          <span className="pill pill-info">Nv. {clubLevel}</span>
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.2rem',
            color: '#fbbf24',
            fontSize: '0.8rem',
            fontWeight: 600,
            textShadow: '0 1px 3px rgba(0,0,0,0.6)',
          }}>
            <Star size={13} fill="currentColor" /> {reputation.toFixed(1)}
          </span>
        </div>
        {!compact && levelInfo && (
          <div style={{
            fontSize: '0.8rem',
            color: 'rgba(255,255,255,0.85)',
            textShadow: '0 1px 3px rgba(0,0,0,0.6)',
            marginTop: '0.15rem',
          }}>
            {levelInfo.description}
          </div>
        )}
      </div>
    </div>
  )
}
